import axios from "axios";
import { from } from "rxjs";

import { CacheService } from "./cacheService";
import routingService from "./routingService";
import stateService from "./stateService";
import oidcService from "./oidcService";
import linkSession from "./linkSessionService";
import {
  stateEvents,
  defaults,
  states,
  verifyStatuses,
} from "../models/imports";

class AuthService extends CacheService {
  constructor() {
    super();

    // identity changed (login/logout/other realm) -> drop the cached userInfo
    oidcService.onUserChange((user, previous) => {
      if ((user && user.profile && user.profile.sub) !== (previous && previous.profile && previous.profile.sub)) {
        this.clearCache("userInfo");
      }
    });

    // session created or destroyed in another tab -> behave like a fresh load;
    // a tab holding a document link keeps its link session whatever happens,
    // and a tab with a session of its own (an organization share) keeps that
    if (typeof window !== "undefined") {
      window.addEventListener("storage", (e) => {
        const key = e.key || "";
        if (key !== "validatege.oidc.realm" && !key.startsWith("oidc.user:")) return;
        if (linkSession.has() || oidcService.isTabScoped()) return;
        const wasAuthenticated = this.isAuthenticated();
        oidcService.syncFromStorage().then(() => {
          if (wasAuthenticated !== this.isAuthenticated()) window.location.reload();
        });
      });
    }
  }

  // bootstrap: finish a login callback or load the stored session
  init() {
    return oidcService.restore();
  }

  get callbackError() {
    return oidcService.callbackError;
  }

  // ---- session state ---------------------------------------------------

  // this tab holds a document link's session (share link or public view)
  hasLinkSession() {
    return linkSession.has();
  }

  // a document link's session, a non-expired access token or a refresh token
  isAuthenticated() {
    if (linkSession.has()) return true;
    const user = oidcService.currentUser();
    return !!user && (!user.expired || !!user.refresh_token);
  }

  // verified account, as reported by api/private/userInfo
  isActive() {
    const info = stateService.getState(states.userInfo).value;
    return !!info && info.userStatus == verifyStatuses.verified;
  }

  // ---- flows -----------------------------------------------------------

  // { realm, returnTo, idpHint, register } -> top-level redirect to Keycloak
  login(options) {
    return oidcService.login(options);
  }

  // ends the Keycloak session; the browser comes back on /landing
  logout() {
    this.clearCache();
    stateService.event(stateEvents.signOut, true);
    return oidcService.logout();
  }

  // forget the Keycloak session locally (refresh failures, errors); signs
  // every tab out, so a document link never calls it
  localSignOut(closeWSConnection = true) {
    this.clearCache();
    stateService.event(stateEvents.signOut, closeWSConnection);
    return oidcService.localSignOut();
  }

  // kept for old call sites; means "local sign-out", not a Keycloak logout
  signOut(closeWSConnection = true) {
    return this.localSignOut(closeWSConnection);
  }

  // ---- axios -----------------------------------------------------------

  // Bearer header only for API calls (never for translation assets or
  // document downloads); refreshes proactively; retries once after a 401.
  // A tab holding a document link sends the link's token, never the Keycloak
  // one, and follows the link's rules (linkSessionService).
  registerAuthInterceptor() {
    const isApiCall = (config) =>
      typeof config.url === "string" && config.url.startsWith(globalConfig.api_URL);

    const requestInterceptor = axios.interceptors.request.use(async (config) => {
      config.headers = config.headers || {};
      delete config.headers.noToken;
      if (config.anonymous || !isApiCall(config)) return config;

      if (linkSession.has()) {
        // out of the link's scope: never sent (it would answer 403 LINK_SCOPE)
        if (!linkSession.inScope(config)) {
          throw new axios.Cancel(`not called in a document link's session: ${config.method} ${config.url}`);
        }
        const token = await linkSession.token();
        if (token) config.headers.Authorization = `Bearer ${token}`;
        return config;
      }

      const user = await oidcService.ensureFreshUser(30).catch(() => null);
      if (user && user.access_token) {
        config.headers.Authorization = `Bearer ${user.access_token}`;
      }
      return config;
    });

    const responseInterceptor = axios.interceptors.response.use(
      (response) => response,
      async (error) => {
        const config = error.config || {};
        const status = error.response && error.response.status;

        if (linkSession.has() && !config.anonymous && isApiCall(config)) {
          // 403 SHARE_WITHDRAWN, 410 DOCUMENT_DELETED, a public view's
          // LINK_SCOPE: the session ends with its page, no re-exchange
          if (linkSession.handleRefusal(error)) return Promise.reject(error);
          if (status !== 401) return Promise.reject(error);
          // a second 401 with a fresh token ends the page, no loop
          if (config._linkRetried) {
            linkSession.end("SESSION_ENDED");
            return Promise.reject(error);
          }
          config._linkRetried = true;
          const slot = await linkSession.renew();
          if (!slot) return Promise.reject(error); // refused: its page shows
          config.headers = config.headers || {};
          config.headers.Authorization = `Bearer ${slot.accessToken}`;
          return axios(config);
        }

        if (
          status !== 401 ||
          config.anonymous ||
          config._retried ||
          !isApiCall(config) ||
          !oidcService.currentUser()
        ) {
          return Promise.reject(error);
        }

        config._retried = true;
        try {
          const user = await oidcService.refreshSingleFlight();
          if (!user) throw error;
          config.headers = config.headers || {};
          config.headers.Authorization = `Bearer ${user.access_token}`;
          return axios(config);
        } catch (e) {
          await this.localSignOut();
          routingService.push(defaults.notAuthRoute);
          return Promise.reject(error);
        }
      }
    );

    return {
      unsubscribe: () => {
        axios.interceptors.request.eject(requestInterceptor);
        axios.interceptors.response.eject(responseInterceptor);
      },
    };
  }

  // ---- API -------------------------------------------------------------

  // the session's start; its callers show a refusal themselves (the
  // session-start page, a document link's page)
  userInfo() {
    return this.fromCache(
      "userInfo",
      () => {
        let promise = axios.get(`${globalConfig.api_URL}api/private/userInfo`, {
          objectResponse: true,
          skipCustomErrorHandling: true,
        });
        return from(promise);
      },
      0.5
    );
  }
}

export default new AuthService();
